export type DineInTableStatus = "Available" | "Busy" | string;
export type DineInSessionStatus = "Open" | "Paid" | "Cancelled" | string;

/** Salon görünümündeki masanın açık oturum özeti (küçük tutulur; ad/para birimi adı yok) */
export interface IDineInOpenSessionSummary {
  orderCode: string;
  openedAtUtc: string;
  openedByUserId: string;
  lineCount: number;
  totalAmount: number;
  /** Hesabın ilk satırı belirler; satırsız oturumda gelmez */
  currencyId?: string;
  currencyDisplayText?: string;
}

export interface IDineInFloorTable {
  tableId: string;
  tableNumber: number;
  label?: string;
  capacity: number;
  minCapacity?: number;
  status: DineInTableStatus;
  /** status === "Available" iken gelmez */
  openSession?: IDineInOpenSessionSummary;
}

export interface IDineInFloor {
  restourantId: string;
  tables: IDineInFloorTable[];
}

export interface IDineInSession {
  orderCode: string;
  restourantId: string;
  tableId: string;
  tableNumber: number;
  tableLabel?: string;
  status: DineInSessionStatus;
  openedByUserId: string;
  openedByUserName?: string;
  openedAtUtc: string;
  closedAtUtc?: string;
  closedByUserId?: string;
  closedByUserName?: string;
  closedTotalAmount?: number;
  /** Yalnızca ödenmiş (Paid) oturumlarda gelir */
  paymentMethodId?: string;
  paymentMethodNameTr?: string;
  paymentMethodNameEn?: string;
  currencyId?: string;
  currencyName?: string;
  currencyDisplayText?: string;
}

export interface IDineInBillLineUpgrade {
  id: string;
  /** Menü eki sonradan kaldırıldıysa gelmez */
  menuUpgradeId?: string;
  title: string;
  unitPrice: number;
  quantity: number;
  lineAmount: number;
}

export interface IDineInBillLine {
  lineId: string;
  foodId: string;
  foodNameTr: string;
  foodNameEn: string;
  quantity: number;
  /** Eklenme anındaki menü fiyatı */
  unitPrice: number;
  /** Kampanyalı birim fiyat; kampanya yoksa unitPrice'a eşit */
  discountedUnitPrice: number;
  /** Yalnız kampanyalı satırda gelir */
  foodDiscountId?: string;
  upgradeTotalAmount: number;
  lineTotal: number;
  lineDiscountAmount: number;
  foodOrderNote?: string;
  upgrades: IDineInBillLineUpgrade[];
  addedByUserId: string;
  addedByUserName?: string;
  addedAtUtc: string;
  /** Yalnız düzenlenmiş satırda gelir */
  updatedByUserId?: string;
  updatedByUserName?: string;
  updatedAtUtc?: string;
  /** Mutfak/servis durumu. Hub yükünde sayı (0/1/2) da gelebilir — normalizeLineStatus ile okuyun. */
  status?: DineInLineStatus | number;
  /** Yalnız Ready'den geçmiş satırda (Preparing → Served atlamasında hiç olmaz) */
  readyAtUtc?: string;
  /** Yalnız Served satırda; durumdan önce ödenmiş eski hesaplarda Served iken bile boş olabilir */
  servedAtUtc?: string;
  /** Yalnız durumu en az bir kez değiştirilmiş satırda */
  statusChangedByUserId?: string;
  statusChangedByUserName?: string;
}

/** Satırın yeri: Hazırlanıyor → Çıktı → Teslim edildi. Yalnız ileri gider. */
export type DineInLineStatus = "Preparing" | "Ready" | "Served";

export const LINE_STATUS_ORDER: DineInLineStatus[] = ["Preparing", "Ready", "Served"];

export const LINE_STATUS_META: Record<DineInLineStatus, { label: string; icon: string; cls: string }> = {
  Preparing: { label: "Hazırlanıyor",  icon: "bi-fire",          cls: "dinein-status--preparing" },
  Ready:     { label: "Çıktı",         icon: "bi-bell-fill",     cls: "dinein-status--ready" },
  Served:    { label: "Teslim edildi", icon: "bi-check2-circle", cls: "dinein-status--served" },
};

/**
 * Durumu isme çevirir. MVC yanıtı isim, hub yükü sayı gönderebilir (doküman: "isimle karşılaştır,
 * sayıya tolerans bırak"). Alan yoksa (durumdan önceki istemci/sunucu) Preparing sayılır.
 */
export function normalizeLineStatus(value: unknown): DineInLineStatus {
  if (value === 1 || value === "1" || value === "Ready") return "Ready";
  if (value === 2 || value === "2" || value === "Served") return "Served";
  return "Preparing";
}

export interface IDineInBill {
  session: IDineInSession;
  lines: IDineInBillLine[];
  /** Kampanyasız toplam */
  subTotalAmount: number;
  totalDiscountAmount: number;
  /** Misafirin ödeyeceği tutar */
  totalAmount: number;
  /** true → oturum kapanmış; ekle/düzenle/sil/öde gösterilmemeli */
  isReadOnly: boolean;
}

export interface IDineInNewLineUpgrade {
  menuUpgradeId: string;
  quantity: number;
}

export interface IDineInNewLine {
  foodId: string;
  quantity: number;
  foodOrderNote?: string;
  upgrades?: IDineInNewLineUpgrade[];
}

export interface IDineInHistoryPage {
  /** Bütün salon sorgulandığında gelmez — masa bilgisi her satırın session'ında */
  tableId?: string;
  tableNumber?: number;
  restourantId?: string;
  items: IDineInBill[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  fromUtc: string;
  toUtc: string;
}

/** Altı olayın tamamı bu yükü taşır; hangi olay olduğu adından okunur. */
export interface IDineInEventPayload {
  orderSource: string;
  orderCode: string;
  restourantId: string;
  tableId: string;
  tableNumber: number;
  tableLabel?: string;
  sessionStatus: DineInSessionStatus | number;
  tableStatus: DineInTableStatus | number;
  totalAmount: number;
  currencyId?: string;
  currencyDisplayText?: string;
  /** Satırla ilgili olmayan olaylarda gelmez — undefined ise eski değeri koru */
  lineCount?: number;
  changedLines: IDineInBillLine[];
  actorUserId: string;
  occurredAtUtc: string;
}

export type DineInEventName =
  | "DineInSessionOpened"
  | "DineInOrderLinesAdded"
  | "DineInOrderLineUpdated"
  | "DineInOrderLineRemoved"
  | "DineInSessionPaid"
  | "DineInTableReleased"
  | "DineInOrderLineStatusChanged";

/**
 * Salon hesabının kapatılabileceği ödeme yöntemleri.
 * Sunucu yalnızca bu ikisini kabul eder (PaymentMetodConstants.DineInPaymentMethodIds);
 * başkası gönderilirse `PaymentMethodNotAllowed` döner.
 */
export const DINEIN_PAYMENT_METHODS = [
  { id: "a1b2c3d4-e5f6-7890-abcd-ef1234567892", label: "Nakit", icon: "bi-cash-stack" },
  { id: "a1b2c3d4-e5f6-7890-abcd-ef1234567894", label: "Kart",  icon: "bi-credit-card-2-front-fill" },
] as const;

/** Bir istekte gönderilebilecek en çok satır (sunucu sınırı). */
export const DINEIN_MAX_LINES_PER_REQUEST = 100;
/** Bir satırdaki en çok adet. */
export const DINEIN_MAX_QUANTITY = 100;
